import { useEffect, useRef, type ReactNode } from 'react';

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEsc = true,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  // Cuando false, el modal NO se cierra al hacer clic fuera / pulsar Escape:
  // solo se sale con el botón Cerrar o la X. Útil en formularios de edición.
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
}) {
  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, closeOnEsc]);

  // Para evitar el bug típico de "selecciono texto dentro, suelto fuera, se
  // cierra el modal": solo cerramos si el gesto EMPEZÓ y TERMINÓ sobre el
  // backdrop. Si el mousedown empieza dentro del contenido (texto, input,
  // botón) ignoramos el click aunque el mouseup caiga fuera al arrastrar.
  const mousedownOnBackdrop = useRef(false);

  if (!open) return null;

  const sizes = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(e) => {
        mousedownOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // Cierra SOLO si el gesto completo (down + up) ocurrió en el fondo y
        // el modal permite cierre por backdrop.
        if (closeOnBackdrop && mousedownOnBackdrop.current && e.target === e.currentTarget) {
          onClose();
        }
        mousedownOnBackdrop.current = false;
      }}
    >
      <div
        // Nunca más alto que la ventana: el cuerpo hace scroll y cabecera y
        // botones (Guardar / Cancelar) quedan siempre a la vista.
        className={`flex max-h-[calc(100vh-2rem)] w-full ${sizes[size]} flex-col rounded-lg bg-white shadow-xl ring-1 ring-slate-900/10`}
        // Mantener el stopPropagation por seguridad (selección dentro de un
        // input que dispara onClick — no debería propagarse al backdrop).
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 cursor-pointer"
            aria-label="Cerrar"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex shrink-0 justify-end gap-2 rounded-b-lg border-t border-slate-200 bg-slate-50 px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
